import { inject, Injectable, Injector } from '@angular/core';
import { map } from 'rxjs';
import { TenantNames } from '@mynexus/mynexus-ui-lib';
import { SendFaxBaseService, SendFaxDocumentTypeConfig } from '@core/services/send-fax/send-fax-base.service';
import { AuthorizationDetail, DocumentFileType, DocumentType, IAuthorizationDTO } from '@core/model';
import { IDocumentResponseDTO } from '@products/dmepos/models';
import DocumentTypeConfig from './document-types';
import { AuthenticationService } from '@core/services';
import { FeatureToggleService } from '@mynexus/mynexus-ui-lib';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef } from '@angular/core';
import { HHDocBuilderDocumentTypes } from '../../constants/document.constants';
import { PlatformService } from '@core/services/platform.service';

@Injectable({
  providedIn: 'root'
})
export class SendFaxHHService extends SendFaxBaseService {
  documentTypeConfig: SendFaxDocumentTypeConfig = DocumentTypeConfig;

  private readonly authenticationService = inject(AuthenticationService);
  private readonly featureToggleService = inject(FeatureToggleService);
  private isDocBuilderForManualFaxes = false;
  private readonly destroyRef = inject(DestroyRef);
  private readonly platformService = inject(PlatformService);
  hh_ProductId: string;

  constructor(injector: Injector) {
    super(injector);
    this.featureToggleService.getFeatureAccess('HH_DocumentBuilder_ManualFaxes')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isEnabled) => {
        this.isDocBuilderForManualFaxes = isEnabled;
      });

    this.platformService.getHHProductWithLinesOfBusiness()
      .subscribe(product => this.hh_ProductId = product?.productId);
  }

  private getBaseUrl(tenantId: string, id: string): string {
    return `${this.apiUrl}/tenants/${tenantId}/authorizations/${id}`;
  }

  sendLetter(payload: any, authorizationId: string, tenantId?: string) {
    return this.http.post<boolean>(`${this.getBaseUrl(tenantId, authorizationId)}/Letter/CreateDocument`, payload);
  }

  sendFax(payload: any, authorizationId: string, tenantId: string) {
    const url = `${this.apiUrl}/tenants/${tenantId}/Fax/`;
    const body = {
      ...payload,
      tenantId: tenantId,
      tenantName: payload.tenantName,
      authorizationId: authorizationId,
      documentTypeId: payload.documentTypeId,
      ...(!this.isDocBuilderForManualFaxes && !HHDocBuilderDocumentTypes.includes(payload.documentType) && {
        documentType: payload.documentTypeName,
      }),
      authNumber: payload.authorizationNumber,
      fileDescription: payload.documentTypeName,
      healthPlanId: tenantId,
      benefitPlanId: payload.benefitPlanId,
      createdBy: this.authenticationService.userEmail,
      clientTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      productId: this.hh_ProductId,
    }
    if (this.isDocBuilderForManualFaxes && HHDocBuilderDocumentTypes.includes(payload.documentType)) {
      return this.http.post<any>(url + 'CreateFax', body)
    }
    else {
      return this.http.post<any>(url + 'SendFax', body)
    }
  }

  getDocumentTypes(authorization?: IAuthorizationDTO, tenantName?: TenantNames): DocumentFileType[] | any {
    return this.http.get<DocumentType[]>(`${this.apiUrl}/Utilities/FaxDocumentTypes`)
      .pipe(map(types => this.documentTypeConfig ? this.mapDocumentByTenantName(types, tenantName) : types));
  }

  filterDocuments(documents: IDocumentResponseDTO[]): IDocumentResponseDTO[] {
    return documents;
  }

  /**
   * Maps HH Authorization details to match shared data contract
   * (For now defined by PAC as PAC and HH are using different contracts)
   * @param a
   */
  override mapAuthorizationDetails(a: AuthorizationDetail) {
    return {
      authorizationDetailId: a.authorizationDetailId,
      serviceNumber: a.sequenceNumber,
      createdDate: a.createdOn,
      requestReceivedDate: a.dateTimeFaxRecieved,
      urgency: a.urgency,
      daysRequested: a.requestedAmount,
      daysApproved: a.approvedAmount,
      effectiveStartDate: a.effectiveFromDate,
      effectiveEndDate: a.effectiveToDate,
      serviceStatus: a.serviceStatus,
      decisionDate: a.decisionDate,
      urgencyChangeDate: a.urgencyDowngradeDate,
      extensionTakenDate: a.extensionDateTime,
      serviceStatusReason: a.serviceStatusReason
    }
  }

  private mapDocumentByTenantName(types: DocumentType[], tenantName: TenantNames) {
    const tenantConfig = this.documentTypeConfig[tenantName];

    return types.filter(type =>
      (!tenantConfig.mailOnly || type.isMail) &&
      !tenantConfig.exclude.includes(type.documentTypeCode)
    );
  }
}
